<template>
  <div>
    <b-card class="mb-1">
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <b-button
          variant="primary"
          @click="viewMapsByCurrentLocations"
        >
          عرض المكتبات الخرائطية
        </b-button>
        <div class="d-flex flex-wrap">
          <b-button
            v-if="canUploadAdministrativeBoundaries"
            variant="outline-primary"
            class="mr-50 mb-50"
            @click="openAdminBoundariesUploadModal"
          >
            رفع الحدود الإدارية
          </b-button>
          <b-button
            variant="primary"
            class="mb-50"
            @click="openUploadModal"
          >
            إضافة مكتبة خرائط
          </b-button>
        </div>
      </div>

      <b-row class="mt-1">
        <b-col
          md="3"
          sm="6"
          class="mb-1"
        >
          <v-select
            v-model="filters.governorate_id"
            :options="governorates.data"
            :reduce="val => val.id"
            :get-option-label="option => option.text"
            placeholder="المحافظة"
          >
            <template #option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
            <template #selected-option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
          </v-select>
        </b-col>
        <b-col
          md="3"
          sm="6"
          class="mb-1"
        >
          <v-select
            v-model="filters.center_id"
            :options="centers.data"
            :reduce="val => val.id"
            :get-option-label="option => option.text"
            placeholder="المركز"
          >
            <template #option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
            <template #selected-option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
          </v-select>
        </b-col>
        <b-col
          md="3"
          sm="6"
          class="mb-1"
        >
          <v-select
            v-model="filters.city_id"
            :options="cities.data"
            :reduce="val => val.id"
            :get-option-label="option => option.text"
            placeholder="المدينة"
          >
            <template #option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
            <template #selected-option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
          </v-select>
        </b-col>
        <b-col
          md="3"
          sm="6"
          class="mb-1"
        >
          <v-select
            v-model="filters.district_id"
            :options="districts.data"
            :reduce="val => val.id"
            :get-option-label="option => option.text"
            placeholder="الحي"
          >
            <template #option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
            <template #selected-option="{ name_ar }">
              <span class="ml-50"> {{ name_ar }}</span>
            </template>
          </v-select>
        </b-col>
      </b-row>
    </b-card>
    <b-card>

      <b-table
        striped
        hover
        responsive
        :items="mapLibraries.data"
        :fields="tableFields"
        :busy="load"
        empty-text="لا توجد سجلات"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner
              small
              class="mr-50"
            />
            <strong>جاري التحميل...</strong>
          </div>
        </template>
      </b-table>
      <div class="d-flex justify-content-center mt-2">
        <b-pagination
          v-model="pagination.page"
          :per-page="pagination.perPage"
          :total-rows="totalItems"
          align="center"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <b-modal
      id="map-library-upload"
      v-model="showUploadModal"
      title="إضافة مكتبة خرائط"
      hide-footer
      @hide="resetUploadForm"
    >
      <b-form @submit.prevent="submitUpload">
        <b-form-group
          label="ملف المضغوط (ZIP)"
          label-for="upload-file"
        >
          <b-form-file
            id="upload-file"
            v-model="uploadForm.file"
            accept=".zip"
            :state="Boolean(uploadForm.file)"
            placeholder="اختر ملف ZIP أو اسحبه هنا"
            drop-placeholder="اسحب الملف هنا..."
          />
        </b-form-group>

        <div class="d-flex justify-content-end flex-wrap">
          <b-button
            variant="outline-secondary"
            :disabled="uploading"
            @click="showUploadModal = false"
          >
            إلغاء
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="uploading"
          >
            <b-spinner
              v-if="uploading"
              small
              class="mr-50"
            />
            حفظ
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      id="admin-boundaries-upload"
      v-model="showAdminBoundariesUploadModal"
      title="رفع الحدود الإدارية"
      hide-footer
      @hide="resetAdminBoundariesUploadForm"
    >
      <b-form @submit.prevent="submitAdminBoundariesUpload">
        <b-form-group
          label="ملف الحدود (ZIP / KMZ / KML)"
          label-for="admin-boundaries-file"
        >
          <b-form-file
            id="admin-boundaries-file"
            v-model="adminBoundariesUploadForm.file"
            accept=".zip,.kmz,.kml,application/zip,application/vnd.google-earth.kmz,application/vnd.google-earth.kml+xml,application/octet-stream"
            :state="Boolean(adminBoundariesUploadForm.file)"
            placeholder="اختر ملف ZIP أو KMZ أو KML أو اسحبه هنا"
            drop-placeholder="اسحب الملف هنا..."
          />
        </b-form-group>
        <b-form-group
          label="نوع الطبقة"
          label-for="admin-boundaries-type"
        >
          <b-form-select
            id="admin-boundaries-type"
            v-model="adminBoundariesUploadForm.type"
            :options="adminBoundaryTypeSelectOptions"
            :state="Boolean(adminBoundariesUploadForm.type)"
            required
          />
        </b-form-group>
        <b-form-group
          label="السنة"
          label-for="admin-boundaries-year"
        >
          <b-form-input
            id="admin-boundaries-year"
            v-model.number="adminBoundariesUploadForm.year"
            type="number"
            min="1900"
            max="2100"
          />
        </b-form-group>
        <div class="d-flex justify-content-end flex-wrap">
          <b-button
            variant="outline-secondary"
            class="mr-50"
            :disabled="adminBoundariesUploading"
            @click="showAdminBoundariesUploadModal = false"
          >
            إلغاء
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="adminBoundariesUploading || !adminBoundariesUploadForm.file || !adminBoundariesUploadForm.type"
          >
            <b-spinner
              v-if="adminBoundariesUploading"
              small
              class="mr-50"
            />
            رفع
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import {
  BTable,
  BButton,
  BCard,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BFormFile,
  BFormInput,
  BModal,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import {
  DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
  ADMINISTRATIVE_BOUNDARY_TYPES,
  fetchAdministrativeBoundaryTypes,
  uploadAdministrativeBoundaries,
} from '@/utils/administrativeBoundaries'

export default {
  name: 'MapLibrary',
  components: {
    BTable,
    BButton,
    BCard,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormFile,
    BFormInput,
    BModal,
    BSpinner,
    BPagination,
    vSelect,
  },
  data() {
    return {
      filters: {
        governorate_id: null,
        center_id: null,
        city_id: null,
        district_id: null,
      },
      uploadForm: {
        file: null,
      },
      showUploadModal: false,
      uploading: false,
      showAdminBoundariesUploadModal: false,
      adminBoundariesUploading: false,
      adminBoundariesUploadForm: {
        file: null,
        type: 'governorate',
        year: DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
      },
      adminBoundaryTypes: ADMINISTRATIVE_BOUNDARY_TYPES.map(item => ({ ...item })),
      pagination: {
        page: 1,
        perPage: 15,
      },
      tableFields: [
        { key: 'governorate.name_ar', label: 'المحافظة' },
        { key: 'center.name_ar', label: 'المركز' },
        { key: 'city.name_ar', label: 'المدينة' },
        { key: 'district.name_ar', label: 'الحي' },
      ],
    }
  },
  computed: {
    canUploadAdministrativeBoundaries() {
      try {
        return typeof this.hasPermission === 'function'
          && this.hasPermission('upload administrative_boundaries')
      } catch (e) {
        return false
      }
    },
    adminBoundaryTypeSelectOptions() {
      return [
        { value: null, text: 'اختر نوع الطبقة', disabled: true },
        ...this.adminBoundaryTypes.map(item => ({
          value: item.type,
          text: item.title,
        })),
      ]
    },
    ...mapGetters({
      mapLibraries: 'mapLibraries/mapLibraries',
      load: 'mapLibraries/load',
      governorates: 'settings/governorates',
      centers: 'settings/centers',
      cities: 'settings/cities',
      districts: 'settings/districts',
    }),
    filteredCenters() {
      return this.filterCenters(this.centers, this.filters.governorate_id)
    },
    filteredCities() {
      return this.filterCities(this.cities, this.filters.center_id)
    },
    filteredDistricts() {
      return this.filterDistricts(this.districts, this.filters.city_id)
    },
    governorateOptions() {
      return this.buildOptions(this.governorates, 'المحافظة')
    },
    centerOptions() {
      return this.buildOptions(this.filteredCenters, 'المركز')
    },
    cityOptions() {
      return this.buildOptions(this.filteredCities, 'المدينة')
    },
    districtOptions() {
      return this.buildOptions(this.filteredDistricts, 'الحي')
    },

    mapLibraryMeta() {
      if (Array.isArray(this.mapLibraries)) return {}
      return this.mapLibraries?.meta || {}
    },
    mapLibraryData() {
      if (Array.isArray(this.mapLibraries)) return this.mapLibraries
      return this.mapLibraries?.data || []
    },
    totalItems() {
      return this.mapLibraryMeta.total || (Array.isArray(this.mapLibraryData) ? this.mapLibraryData.length : 0)
    },
    formattedMapLibraries() {
      return (this.mapLibraryData || []).map(item => ({
        ...item,
        governorate_name: item?.governorate?.name || item?.governorate_name || '-',
        center_name: item?.center?.name || item?.center_name || '-',
        city_name: item?.city?.name || item?.city_name || '-',
        district_name: item?.district?.name || item?.district_name || '-',
      }))
    },
  },
  watch: {
    'filters.governorate_id': function (val) {
      if (!val) {
        this.filters.center_id = null
        this.applyFilters()
      }
      this.filters.city_id = null
      this.filters.district_id = null
      this.applyFilters()
    },
    'filters.center_id': function () {
      this.filters.city_id = null
      this.filters.district_id = null
      this.applyFilters()
    },
    'filters.city_id': function () {
      this.filters.district_id = null
      this.applyFilters()
    },
    'filters.district_id': function () {
      this.applyFilters()
    },

    mapLibraries(val) {
      const meta = val?.meta
      if (meta) {
        this.pagination.page = meta.current_page || this.pagination.page
        this.pagination.perPage = meta.per_page || this.pagination.perPage
      }
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    viewMapsByCurrentLocations() {
      this.$router.push({ name: 'mapLibraryView' })
    },
    init() {
      this.fetchMapLibraries()
      this.$store.dispatch('settings/listGovernorates', { query: {} })
      this.$store.dispatch('settings/listCenters', { query: {} })
      this.$store.dispatch('settings/listCities', { query: {} })
      this.$store.dispatch('settings/listDistricts', { query: {} })
    },
    buildOptions(list, placeholder) {
      const base = [{ value: null, text: `اختر ${placeholder}` }]
      if (!Array.isArray(list)) return base
      return base.concat(list.map(item => ({ value: item.id, text: item.name })))
    },
    filterCenters(list, governorateId) {
      if (!Array.isArray(list)) return []
      if (!governorateId) return list
      return list.filter(center => center.governorate_id === governorateId)
    },
    filterCities(list, centerId) {
      if (!Array.isArray(list)) return []
      if (!centerId) return list
      return list.filter(city => city.center_id === centerId)
    },
    filterDistricts(list, cityId) {
      if (!Array.isArray(list)) return []
      if (!cityId) return list
      return list.filter(district => district.city_id === cityId)
    },
    cleanPayload(payload) {
      const sanitized = Object.entries(payload || {}).reduce((acc, [key, value]) => {
        if (value) acc[key] = value
        return acc
      }, {})
      return Object.keys(sanitized).length ? sanitized : undefined
    },
    fetchMapLibraries() {
      const filtersPayload = this.cleanPayload(this.filters) || {}
      const payload = {
        ...filtersPayload,
        page: this.pagination.page,
        per_page: this.pagination.perPage,
      }
      this.$store.dispatch('mapLibraries/listMapLibraries', payload)
    },
    applyFilters() {
      this.pagination.page = 1
      this.fetchMapLibraries()
    },
    resetFilters() {
      this.filters = {
        governorate_id: null,
        center_id: null,
        city_id: null,
        district_id: null,
      }
      this.pagination.page = 1
      this.fetchMapLibraries()
    },
    onPageChange(page) {
      this.pagination.page = page
      this.fetchMapLibraries()
    },
    openUploadModal() {
      this.showUploadModal = true
    },
    resetUploadForm() {
      this.uploadForm = {
        file: null,
      }
      this.uploading = false
    },
    openAdminBoundariesUploadModal() {
      if (!this.canUploadAdministrativeBoundaries) return
      this.prefetchAdministrativeBoundaryTypes()
      this.adminBoundariesUploadForm = {
        file: null,
        type: this.adminBoundariesUploadForm?.type || 'governorate',
        year: DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
      }
      this.showAdminBoundariesUploadModal = true
    },
    resetAdminBoundariesUploadForm() {
      this.adminBoundariesUploadForm = {
        file: null,
        type: this.adminBoundariesUploadForm?.type || 'governorate',
        year: DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR,
      }
      this.adminBoundariesUploading = false
    },
    async prefetchAdministrativeBoundaryTypes() {
      try {
        this.adminBoundaryTypes = await fetchAdministrativeBoundaryTypes()
      } catch (error) {
        this.adminBoundaryTypes = ADMINISTRATIVE_BOUNDARY_TYPES.map(item => ({ ...item }))
      }
    },
    async submitAdminBoundariesUpload() {
      if (!this.canUploadAdministrativeBoundaries) return
      if (!this.adminBoundariesUploadForm.file) {
        this.$bvToast?.toast('يرجى اختيار ملف ZIP أو KMZ أو KML', {
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      if (!this.adminBoundariesUploadForm.type) {
        this.$bvToast?.toast('يرجى اختيار نوع الطبقة', {
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      this.adminBoundariesUploading = true
      try {
        const type = this.adminBoundariesUploadForm.type
        const year = this.adminBoundariesUploadForm.year || DEFAULT_ADMINISTRATIVE_BOUNDARIES_YEAR
        const result = await uploadAdministrativeBoundaries(
          this.adminBoundariesUploadForm.file,
          { type, year },
        )
        const payload = result?.data && typeof result.data === 'object' ? result.data : result
        this.showAdminBoundariesUploadModal = false
        this.resetAdminBoundariesUploadForm()
        if (payload?.queued) {
          this.$bvToast?.toast(payload.message || 'سيتم إشعارك عند الانتهاء', {
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-center',
          })
          return
        }
        const created = payload?.created ?? 0
        const updated = payload?.updated ?? 0
        const total = payload?.total ?? ((Number(created) || 0) + (Number(updated) || 0))
        const label = payload?.type_label_ar || type
        this.$bvToast?.toast(
          `تم استيراد ${label} — جديد: ${created} | محدّث: ${updated} | الإجمالي: ${total}`,
          {
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-center',
          },
        )
      } catch (error) {
        this.$bvToast?.toast(
          error?.response?.data?.message || 'فشل رفع ملف الحدود الإدارية',
          {
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-top-center',
          },
        )
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        this.adminBoundariesUploading = false
      }
    },
    async submitUpload() {
      if (!this.uploadForm.file) {
        this.$bvToast?.toast('يرجى اختيار ملف ZIP', {
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-center',
        })
        return
      }
      this.uploading = true
      const formData = new FormData()
      formData.append('file', this.uploadForm.file)
      try {
        await this.$store.dispatch('mapLibraries/uploadMapLibrary', formData)
        this.showUploadModal = false
        this.resetUploadForm()
        this.fetchMapLibraries()
        this.$bvToast?.toast('تم رفع الملف بنجاح', {
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-center',
        })
      } catch (error) {
        this.$bvToast?.toast('فشل رفع الملف، يرجى المحاولة مرة أخرى', {
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-center',
        })
        // surface error for potential upstream handling
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        this.uploading = false
      }
    },
  },
}
</script>
